
import type { CellState, Coord, TerrainType } from '@fleetduel/shared'
import { coordKey } from '@fleetduel/shared'
import { tileWater, markerHit, markerMiss, markerSunk, reticle } from '../../assets/index.js'
import { useUIStore } from '../../store/uiStore.js'
import { TerrainMarker } from './TerrainMarker.js'

interface CellProps {
  coord: Coord
  state: CellState
  isOwnBoard: boolean
  hasShip: boolean
  isInSonarZone: boolean
  previewState?: 'valid' | 'invalid'
  /** Terrain object on this cell (blocked for placement and targeting) */
  terrain?: TerrainType
  /** Theater water tint overlaid on the base water tile */
  waterTint?: string
  onClick?: (coord: Coord) => void
  onHover?: (coord: Coord) => void
}

export const CELL_SIZE = 64

const markerAssets: Partial<Record<CellState, string>> = {
  hit: markerHit,
  miss: markerMiss,
  sunk: markerSunk,
}

const previewColors: Record<'valid' | 'invalid', string> = {
  valid: 'rgba(76, 175, 80, 0.55)',
  invalid: 'rgba(244, 67, 54, 0.55)',
}

const imgAbsolute: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
}

export function Cell({ coord, state, isOwnBoard, hasShip: _hasShip, isInSonarZone, previewState, terrain, waterTint, onClick, onHover }: CellProps) {
  const isBlocked = terrain !== undefined
  const interactive = !!onClick && !isOwnBoard && state === 'unknown' && !isBlocked
  const key = coordKey(coord)
  const hoveredCell = useUIStore(s => s.hoveredCell)
  const setHoveredCell = useUIStore(s => s.setHoveredCell)
  const selectedAction = useUIStore(s => s.selectedAction)
  const isHovered = !isOwnBoard && hoveredCell === key
  const showReticle = isHovered && state === 'unknown' && selectedAction !== 'sonar' && !isBlocked

  const marker = markerAssets[state]

  return (
    <div
      onClick={() => { if (!isBlocked) onClick?.(coord) }}
      onMouseEnter={() => {
        onHover?.(coord)
        if (!isOwnBoard) setHoveredCell(key)
      }}
      onMouseLeave={() => {
        if (!isOwnBoard && hoveredCell === key) setHoveredCell(null)
      }}
      style={{
        width: CELL_SIZE,
        height: CELL_SIZE,
        position: 'relative',
        cursor: interactive ? 'pointer' : 'default',
        overflow: 'hidden',
      }}
    >
      {/* Water tile background */}
      <img src={tileWater} alt="" style={imgAbsolute} draggable={false} />

      {/* Theater water tint (ART_GUIDE_v3 §2, 2D fallback) */}
      {waterTint && (
        <div style={{ ...imgAbsolute, backgroundColor: waterTint }} />
      )}

      {/* Terrain object — blocked cell */}
      {terrain && <TerrainMarker type={terrain} />}

      {/* Sonar zone overlay */}
      {isInSonarZone && (
        <div style={{
          ...imgAbsolute,
          background: 'rgba(61, 220, 151, 0.18)',
        }} />
      )}

      {/* Placement preview overlay */}
      {previewState && (
        <div style={{
          ...imgAbsolute,
          backgroundColor: previewColors[previewState],
          border: `2px solid ${previewState === 'valid' ? '#66bb6a' : '#ef5350'}`,
        }} />
      )}

      {/* State marker (hit/miss/sunk) */}
      {marker && (
        <img src={marker} alt={state} style={imgAbsolute} draggable={false} />
      )}

      {/* Reticle on hover (opponent board, non-sonar actions only) */}
      {showReticle && (
        <img src={reticle} alt="" style={{ ...imgAbsolute, opacity: 0.6 }} draggable={false} />
      )}
    </div>
  )
}
